import { useState, useEffect, useRef } from "react";

const KIBRIT_SYSTEM = `You are the AI assistant for Kibrit AI — a technology company based in Baku, Azerbaijan that builds intelligent systems for modern businesses.

Key facts about Kibrit AI:
- 20+ years of experience in software, automation, and digital solutions
- Products: AzGuard AI (cybersecurity), TurAl (AI travel companion), FitOl (wellness coach), Bildilchin (local AI for Azerbaijan)
- Coming soon: NexOps (operations), Recall (knowledge), VoiceDesk (contact center), SentinelMind (risk)
- Solutions: AI Contact Center, AI Assistants, AI Automation, AI Cybersecurity
- AI Core: 4-layer architecture — Understanding, Data Integration, Reasoning Engine, Execution Layer
- Contact: hello@kibrit.tech | kibrit.tech | Baku, Azerbaijan

Be concise, helpful, and professional. Respond in the same language as the user (Azerbaijani or English). If asked about pricing or demos, invite them to contact via hello@kibrit.tech or use the contact form. Keep answers under 3 sentences unless detail is needed.`;

const FORMSPREE_CONTACT = "https://formspree.io/f/xaqazvnj";
const FORMSPREE_CALL = "https://formspree.io/f/xaqazvnj";

function fallbackChatReply(input) {
  const text = String(input || "").toLowerCase();
  if (text.includes("core") || text.includes("architecture"))
    return "AI Core combines understanding, data integration, reasoning, and execution in one reusable intelligence layer — the backbone of every Kibrit product.";
  if (
    text.includes("cyber") ||
    text.includes("security") ||
    text.includes("azguard")
  )
    return "AzGuard AI provides enterprise-grade anomaly detection, threat monitoring, and incident-response workflows powered by our AI Core.";
  if (
    text.includes("fitol") ||
    text.includes("tural") ||
    text.includes("bildilchin") ||
    text.includes("product")
  )
    return "Kibrit AI products include AzGuard AI (cybersecurity), FitOl (wellness), TurAl (travel), and Bildilchin (localized assistant).";
  if (
    text.includes("demo") ||
    text.includes("contact") ||
    text.includes("call")
  )
    return "Enter your phone number below and we'll reach out to schedule a personalized demo with our team.";
  return "Kibrit AI builds intelligent systems for enterprise operations. Ask me about solutions, products, or request a demo.";
}

function Logo({ height = 28 }) {
  return (
    <svg
      height={height}
      viewBox="0 0 116 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* kibrit — white */}
      <path
        d="M17.1503 28.9202H11.3378L5.29945 20.7354L5.3418 29.0003H0.0423579L0 0.0424805H5.21944V18.0622L10.533 10.6506H16.8114L10.2741 19.0853L17.1503 28.9202Z"
        fill="#FBFCFD"
      />
      <path
        d="M21.8711 8.13754C21.0333 8.13754 20.3038 7.83109 19.692 7.21346C19.0754 6.59583 18.7695 5.86976 18.7695 5.03054C18.7695 4.61093 18.8542 4.21018 19.0237 3.82828C19.1931 3.44639 19.4143 3.11165 19.692 2.82405C19.965 2.53645 20.2944 2.30543 20.6756 2.1357C21.0569 1.96597 21.4522 1.8811 21.8758 1.8811C22.2947 1.8811 22.6994 1.96597 23.0947 2.1357C23.4854 2.30543 23.8195 2.53645 24.0972 2.82405C24.3702 3.11165 24.5961 3.44639 24.7655 3.82828C24.935 4.21018 25.0197 4.61093 25.0197 5.03054C25.0197 5.86976 24.7138 6.60054 24.0972 7.21346C23.4713 7.83109 22.7323 8.13754 21.8711 8.13754ZM19.019 29.0002V10.6128H24.422V29.0002H19.019Z"
        fill="#FBFCFD"
      />
      <path
        d="M46.3254 19.7688C46.3254 21.2351 46.0948 22.56 45.6289 23.7434C45.1629 24.9268 44.4899 25.9404 43.6098 26.7749C42.7297 27.6094 41.6472 28.2553 40.3671 28.708C39.0869 29.1606 37.6279 29.3869 35.9948 29.3869C35.3171 29.3869 34.597 29.4387 33.8628 29.2973C29.434 28.4487 28.2951 26.6995 27.8433 26.3176L27.8856 0H33.1192V11.1173C33.7451 10.8627 34.7853 10.5657 35.463 10.4243C36.1407 10.2828 36.8702 10.2168 37.6515 10.2168C39.0587 10.2168 40.3012 10.4431 41.3837 10.8957C42.4662 11.3483 43.3698 11.9943 44.0993 12.8288C44.8288 13.6633 45.3794 14.6722 45.756 15.8462C46.1372 17.0202 46.3254 18.3261 46.3254 19.7688ZM40.593 19.6274C40.593 16.2799 39.3176 14.9127 36.6537 14.8325C34.2017 14.7571 33.5192 16.1526 33.1662 16.3601L33.1239 23.7245C33.4016 23.7716 34.0275 24.7099 36.3007 24.7099C37.835 24.7099 38.6492 24.4741 39.4258 23.4982C40.2024 22.5222 40.593 21.2351 40.593 19.6274Z"
        fill="#FBFCFD"
      />
      <path
        d="M54.1335 12.4846C54.59 11.32 56.4349 10.1413 59.4706 10.4054V14.9409C53.2863 13.965 54.237 18.5948 54.1335 20.1507V28.8635L48.7964 28.9955V10.6034L54.1335 10.5751V12.4846Z"
        fill="#FBFCFD"
      />
      <path
        d="M62.0073 29.0002V10.6128H67.4103V29.0002H62.0073Z"
        fill="#FBFCFD"
      />
      <path
        d="M64.685 8.49594C63.8473 8.49594 63.1225 8.18949 62.506 7.57186C61.8894 6.95423 61.5835 6.22816 61.5835 5.38894C61.5835 4.96933 61.6682 4.56858 61.8376 4.18668C62.0071 3.80479 62.2283 3.47005 62.506 3.18245C62.7789 2.89485 63.1084 2.66383 63.4896 2.4941C63.8708 2.32437 64.2662 2.2395 64.6897 2.2395C65.1086 2.2395 65.5134 2.32437 65.9087 2.4941C66.2993 2.66383 66.6335 2.89485 66.9112 3.18245C67.1842 3.47005 67.4101 3.80479 67.5795 4.18668C67.7489 4.56858 67.8336 4.96933 67.8336 5.38894C67.8336 6.22816 67.5277 6.95894 66.9112 7.57186C66.2899 8.18949 65.551 8.49594 64.685 8.49594Z"
        fill="#B2CD38"
      />
      <path
        d="M79.9953 14.6157H76.3572V22.6118C76.3572 23.0927 76.4419 23.4699 76.6114 23.7339C76.7808 23.998 77.0396 24.1913 77.3879 24.3139C77.7362 24.4364 77.7221 24.4977 78.2304 24.5119C78.7339 24.526 79.327 24.5072 80 24.4553V29.0003C73.3592 30.509 70.8224 26.1526 70.893 22.6118V15.0919V5.12964H76.4278V10.6081H80V14.6157H79.9953Z"
        fill="#FBFCFD"
      />
      {/* Ai — green #B2CD38 */}
      <path
        d="M106.15 29.0002H100.766L99.351 25.1812H93.072L91.657 29.0002H86.406L93.586 10.5752H98.989L106.15 29.0002ZM97.877 21.0849L96.211 16.5024L94.545 21.0849H97.877Z"
        fill="#B2CD38"
      />
      <path
        d="M109.5873 29.0002V10.6128H114.9903V29.0002H109.5873Z"
        fill="#B2CD38"
      />
      <path
        d="M112.265 8.49594C111.4273 8.49594 110.7025 8.18949 110.086 7.57186C109.4694 6.95423 109.1635 6.22816 109.1635 5.38894C109.1635 4.96933 109.2482 4.56858 109.4176 4.18668C109.5871 3.80479 109.8083 3.47005 110.086 3.18245C110.3589 2.89485 110.6884 2.66383 111.0696 2.4941C111.4508 2.32437 111.8462 2.2395 112.2697 2.2395C112.6886 2.2395 113.0934 2.32437 113.4887 2.4941C113.8793 2.66383 114.2135 2.89485 114.4912 3.18245C114.7642 3.47005 114.9901 3.80479 115.1595 4.18668C115.3289 4.56858 115.4136 4.96933 115.4136 5.38894C115.4136 6.22816 115.1077 6.95894 114.4912 7.57186C113.8699 8.18949 113.131 8.49594 112.265 8.49594Z"
        fill="#B2CD38"
      />
    </svg>
  );
}

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return inView;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mobile;
}

function FadeIn({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      style={{
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

const coreIcons = [
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#B2CD38" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="4" stroke="#B2CD38" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="1.5" fill="#B2CD38" />
  </svg>,
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="#B2CD38"
      strokeWidth="1.5"
    />
    <line x1="3" y1="12" x2="21" y2="12" stroke="#B2CD38" strokeWidth="1.5" />
    <line x1="12" y1="3" x2="12" y2="21" stroke="#B2CD38" strokeWidth="1.5" />
  </svg>,
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#B2CD38" strokeWidth="1.5" />
    <line x1="5" y1="5" x2="19" y2="19" stroke="#B2CD38" strokeWidth="1.5" />
    <line x1="19" y1="5" x2="5" y2="19" stroke="#B2CD38" strokeWidth="1.5" />
  </svg>,
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="#B2CD38"
      strokeWidth="1.5"
    />
    <circle cx="12" cy="12" r="4" fill="#B2CD38" opacity="0.3" />
    <circle cx="12" cy="12" r="2" fill="#B2CD38" />
  </svg>,
];

const coreItems = {
  EN: [
    {
      title: "Understanding",
      desc: "Interprets language, voice, and context to detect intent with precision.",
      detail:
        "Transforms raw inputs — text, voice, structured data — into clean semantic intent, setting the foundation for every downstream decision.",
    },
    {
      title: "Data Integration",
      desc: "Connects CRM, ERP, APIs, and knowledge bases in real time.",
      detail:
        "Retrieves the right data from the right source at the right moment, ensuring every response is grounded in live operational reality.",
    },
    {
      title: "Reasoning Engine",
      desc: "Applies logic, policies, and AI models to generate optimal decisions.",
      detail:
        "Weighs context, constraints, and business rules to surface the best possible action — consistently, at scale.",
    },
    {
      title: "Execution Layer",
      desc: "Triggers workflows, updates systems, and completes tasks automatically.",
      detail:
        "Turns intelligence into impact: creating tickets, sending notifications, orchestrating approvals, and closing the loop across systems.",
    },
  ],
  AZ: [
    {
      title: "Anlama",
      desc: "Niyyəti dəqiq müəyyən etmək üçün dili, səsi və konteksti təhlil edir.",
      detail:
        "Mətn, səs və strukturlaşdırılmış məlumatı aydın mənaya çevirir — hər qərarın düzgün əsasda qurulmasını təmin edir.",
    },
    {
      title: "Məlumat İnteqrasiyası",
      desc: "CRM, ERP, API və bilik bazalarını canlı rejimdə birləşdirir.",
      detail:
        "Hər cavabın real iş məlumatlarına söykənməsi üçün lazım olan məlumatı, lazım olan anda, doğru mənbədən götürür.",
    },
    {
      title: "Qərar Mühərriki",
      desc: "Ən düzgün addımı tapmaq üçün məntiq, qaydalar və AI modelləri tətbiq edir.",
      detail:
        "Konteksti, məhdudiyyətləri və biznes qaydalarını götür-qoy edərək ən optimal hərəkəti — istənilən miqyasda, ardıcıl olaraq — müəyyənləşdirir.",
    },
    {
      title: "İcra Qatı",
      desc: "İş axınlarını işə salır, sistemləri yeniləyir və tapşırıqları avtomatik tamamlayır.",
      detail:
        "Düşüncəni nəticəyə çevirir: biletlər açır, bildirişlər göndərir, təsdiqləri idarə edir və sistemlər arasında dövrəni tamamlayır.",
    },
  ],
};

const solutions = {
  EN: [
    {
      title: "AI Contact Center",
      tag: "Customer Ops",
      desc: "Intelligent routing, real-time guidance, summaries, and service automation across every channel.",
      detail:
        "Faster response times, consistent quality, lower operator load — purpose-built for contact centers that need to scale without friction.",
    },
    {
      title: "AI Assistants",
      tag: "Internal Ops",
      desc: "Employee-facing assistants for documents, knowledge access, and daily operational support.",
      detail:
        "Deployed across departments so teams can find information faster, generate content, and execute with less manual effort.",
    },
    {
      title: "AI Automation",
      tag: "Workflows",
      desc: "Connects systems, orchestrates rules, and automates approvals across enterprise processes.",
      detail:
        "Built for organizations reducing repetitive operational work while gaining full visibility into every automated action.",
    },
    {
      title: "AI Cybersecurity",
      tag: "Security",
      desc: "Anomaly detection, alert analysis, and incident response acceleration for security teams.",
      detail:
        "Prioritizes threats, surfaces suspicious behavior, and turns fragmented signals into actionable intelligence — faster.",
    },
  ],
  AZ: [
    {
      title: "AI Əlaqə Mərkəzi",
      tag: "Müştəri Xidməti",
      desc: "Hər kanalda ağıllı yönləndirmə, anlıq operativ dəstək, avtomatik xülasə və xidmət avtomatlaşdırması.",
      detail:
        "Operatorların yükünü azaldır, cavab sürətini artırır, keyfiyyəti sabit saxlayır — böyümək istəyən əlaqə mərkəzləri üçün.",
    },
    {
      title: "AI Köməkçilər",
      tag: "Daxili İşlər",
      desc: "Sənəd axtarışı, bilik bazasına çıxış və gündəlik iş dəstəyi üçün işçi heyətinə yönəlmiş köməkçilər.",
      detail:
        "Şöbələr üzrə quraşdırılır — komanda üzvləri lazımlı məlumatı daha tez tapır, məzmun hazırlayır, daha az vaxt itirir.",
    },
    {
      title: "AI Avtomatlaşdırma",
      tag: "İş Axınları",
      desc: "Sistemləri bir-birinə bağlayır, qaydaları tətbiq edir və korporativ proseslərdə təsdiqetmə mərhələlərini avtomatlaşdırır.",
      detail:
        "Əl ilə görülən təkrarlı işləri aradan qaldırır, eyni zamanda hər avtomatlaşdırılmış addıma tam nəzarəti saxlayır.",
    },
    {
      title: "AI Kibertəhlükəsizlik",
      tag: "Təhlükəsizlik",
      desc: "Təhlükəsizlik komandaları üçün anomaliya aşkarlanması, xəbərdarlıq analizi və insidentlərə sürətli müdaxilə.",
      detail:
        "Təhdidlərə prioritet verir, şübhəli davranışları üzə çıxarır və dağınıq siqnalları konkret fəaliyyət planına çevirir.",
    },
  ],
};

const products = {
  EN: [
    {
      title: "AzGuard AI",
      tag: "Cybersecurity",
      color: "#EF4444",
      ready: true,
      desc: "Enterprise threat intelligence platform. Detects anomalies, correlates signals, and accelerates incident response through continuous AI monitoring.",
      detail:
        "Turns fragmented security alerts into prioritised, actionable intelligence — reducing time-to-response and analyst fatigue across complex environments.",
    },
    {
      title: "NexOps",
      tag: "Operations",
      color: "#8B5CF6",
      ready: false,
      desc: "AI operations layer for enterprise workflows. Automates approvals, escalations, and cross-system actions based on real-time context and business rules.",
      detail:
        "Connects to existing ERP, CRM, and ticketing systems to orchestrate decisions that previously required manual intervention — at any scale.",
    },
    {
      title: "Recall",
      tag: "Knowledge",
      color: "#06B6D4",
      ready: false,
      desc: "Organisational memory assistant. Surfaces relevant documents, decisions, and institutional knowledge at the moment employees need it most.",
      detail:
        "Trained on internal data, Recall understands context and intent — reducing time lost searching, and ensuring critical knowledge stays accessible.",
    },
    {
      title: "VoiceDesk",
      tag: "Contact Center",
      color: "#F97316",
      ready: false,
      desc: "AI-powered contact center assistant. Provides real-time guidance, auto-summaries, and intelligent routing for every customer interaction.",
      detail:
        "Reduces handle time and improves consistency by giving agents the right information at the right moment — without switching between systems.",
    },
    {
      title: "TurAl",
      tag: "Travel",
      color: "#3B82F6",
      ready: true,
      desc: "AI travel companion. Builds personalised itineraries, manages budgets, and adapts plans in real time based on preferences and constraints.",
      detail:
        "Moves users from a vague idea of a trip to a structured, ready-to-book plan — handling logistics, timing, and local context automatically.",
    },
    {
      title: "FitOl",
      tag: "Wellness",
      color: "#10B981",
      ready: true,
      desc: "Personal AI wellness coach. Tracks habits, guides nutrition, and provides daily accountability to support sustainable lifestyle change.",
      detail:
        "Built as a long-term companion rather than a tracker — FitOl adapts to individual patterns and nudges users toward consistent, healthy behaviour.",
    },
    {
      title: "Bildilchin",
      tag: "Local AI",
      color: "#F59E0B",
      ready: true,
      desc: "Localised AI assistant for the Azerbaijani market. Answers questions, navigates services, and supports everyday digital interactions in context.",
      detail:
        "Designed for users who need a reliable, locally aware assistant — covering government services, utilities, transport, and daily information needs.",
    },
    {
      title: "SentinelMind",
      tag: "Risk",
      color: "#EC4899",
      ready: false,
      desc: "AI risk and compliance monitor. Continuously scans operations, flags deviations, and generates audit-ready reports across regulated industries.",
      detail:
        "Helps compliance teams stay ahead of issues rather than reacting to them — with automated monitoring, alerting, and documentation built in.",
    },
  ],
  AZ: [
    {
      title: "AzGuard AI",
      tag: "Kibertəhlükəsizlik",
      color: "#EF4444",
      ready: true,
      desc: "Korporativ təhdid kəşfiyyatı platforması. Anomaliyaları aşkar edir, siqnalları əlaqələndirir və davamlı AI monitorinqi ilə insidentlərə cavabı sürətləndirir.",
      detail:
        "Dağınıq təhlükəsizlik xəbərdarlıqlarını prioritetli, konkret tədbirlər planına çevirir — mürəkkəb mühitlərdə cavab müddətini və analitik yorğunluğunu azaldır.",
    },
    {
      title: "NexOps",
      tag: "Əməliyyatlar",
      color: "#8B5CF6",
      ready: false,
      desc: "Korporativ iş axınları üçün AI əməliyyat qatı. Real vaxt konteksti əsasında təsdiqlər, eskalasiyalar və sistemlərarası əməliyyatları avtomatik idarə edir.",
      detail:
        "Mövcud ERP, CRM və tapşırıq sistemlərinə qoşularaq əvvəllər əl ilə görülən işləri istənilən miqyasda avtomatik yerinə yetirir.",
    },
    {
      title: "Recall",
      tag: "Bilik Bazası",
      color: "#06B6D4",
      ready: false,
      desc: "Korporativ yaddaş köməkçisi. Lazım olan anda əlaqəli sənədləri, qərarları və şirkət biliyini tapıb işçilərə təqdim edir.",
      detail:
        "Daxili məlumatlar üzərində öyrənilmiş Recall konteksti anlayır — axtarışa sərf olunan vaxtı azaldır, kritik biliyə çıxışı asanlaşdırır.",
    },
    {
      title: "VoiceDesk",
      tag: "Əlaqə Mərkəzi",
      color: "#F97316",
      ready: false,
      desc: "AI dəstəkli əlaqə mərkəzi köməkçisi. Hər müştəri müraciəti üçün anlıq tövsiyə, avtomatik xülasə və ağıllı yönləndirmə.",
      detail:
        "Operatorlara sistem dəyişdirmədən, lazımlı məlumatı lazımlı anda göstərərək müraciət müddətini qısaldır, xidmət keyfiyyətini artırır.",
    },
    {
      title: "TurAl",
      tag: "Səyahət",
      color: "#3B82F6",
      ready: true,
      desc: "AI səyahət köməkçisi. Fərdi marşrutlar qurur, büdcəni idarə edir və planı real vaxtda istəklərə uyğun yeniləyir.",
      detail:
        "İstifadəçini qeyri-müəyyən səyahət ideyasından hazır, sifariş üçün açıq plana çatdırır — logistika, vaxt və yerli detalları avtomatik həll edir.",
    },
    {
      title: "FitOl",
      tag: "Sağlamlıq",
      color: "#10B981",
      ready: true,
      desc: "Şəxsi AI sağlamlıq məşqçisi. Vərdişlərə nəzarət edir, qidalanmada istiqamət verir və sağlam həyat tərzi üçün gündəlik dayaq olur.",
      detail:
        "Sadə izləyici deyil, uzunmüddətli yoldaş kimi qurulub — FitOl hər kəsin öz tempi və xüsusiyyətlərinə uyğunlaşır.",
    },
    {
      title: "Bildilchin",
      tag: "Yerli AI",
      color: "#F59E0B",
      ready: true,
      desc: "Azərbaycan bazarı üçün hazırlanmış lokal AI köməkçisi. Suallara cavab verir, xidmətlərdə istiqamət göstərir, gündəlik rəqəmsal həyatı asanlaşdırır.",
      detail:
        "Dövlət xidmətləri, kommunal ödənişlər, nəqliyyat və gündəlik məlumat ehtiyacları üçün — etibarlı, yerli şəraitə uyğun köməkçi.",
    },
    {
      title: "SentinelMind",
      tag: "Risk",
      color: "#EC4899",
      ready: false,
      desc: "AI risk və uyğunluq monitoru. Əməliyyatları davamlı izləyir, sapmaları qeyd edir, tənzimlənən sənaye üçün audit hesabatları hazırlayır.",
      detail:
        "Uyğunluq komandalarına problemlərə reaksiya vermək əvəzinə onları əvvəlcədən görməyə imkan verir — avtomatik monitorinq, xəbərdarlıq və sənədləşmə ilə.",
    },
  ],
};

function TypedText({ text, style = {} }) {
  const [shown, setShown] = useState("");
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setShown("");
    setIdx(0);
  }, [text]);
  useEffect(() => {
    if (idx >= text.length) return;
    const t = setTimeout(() => {
      setShown(text.slice(0, idx + 1));
      setIdx((i) => i + 1);
    }, 18);
    return () => clearTimeout(t);
  }, [idx, text]);
  return (
    <span style={style}>
      {shown}
      {idx < text.length && (
        <span
          style={{
            display: "inline-block",
            width: 1.5,
            height: "0.9em",
            background: "#B2CD38",
            marginLeft: 2,
            verticalAlign: "middle",
            opacity: 0.8,
          }}
        />
      )}
    </span>
  );
}

function CoreCard({ item, index, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <FadeIn delay={index * 80} style={{ height: "100%", display: "flex" }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          background: expanded ? "#1a2840" : "#162030",
          border: `1px solid ${
            expanded ? "rgba(178,205,56,0.4)" : "rgba(255,255,255,0.07)"
          }`,
          borderRadius: 16,
          padding: "28px 24px",
          cursor: "pointer",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          flex: 1,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {expanded && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background:
                "linear-gradient(90deg, transparent, #B2CD38, transparent)",
            }}
          />
        )}
        <div style={{ width: 24, height: 24, marginBottom: 16, flexShrink: 0 }}>
          {coreIcons[index]}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#fff",
            marginBottom: 8,
            letterSpacing: "0.01em",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.7,
            flex: 1,
          }}
        >
          {item.desc}
        </div>
        {expanded && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              fontSize: 13,
              lineHeight: 1.75,
            }}
          >
            <TypedText
              text={item.detail}
              style={{ color: "rgba(255,255,255,0.6)" }}
            />
          </div>
        )}
        <div
          style={{
            marginTop: 16,
            fontSize: 11,
            color: expanded ? "#B2CD38" : "rgba(255,255,255,0.2)",
            transition: "color 0.2s",
          }}
        >
          {expanded ? t.expandLess : t.expandMore}
        </div>
      </div>
    </FadeIn>
  );
}

function SolutionCard({ item, index, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <FadeIn delay={index * 80}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          background: "#162030",
          border: `1px solid ${
            expanded ? "rgba(178,205,56,0.3)" : "rgba(255,255,255,0.07)"
          }`,
          borderRadius: 16,
          padding: "28px 24px",
          cursor: "pointer",
          transition: "all 0.3s ease",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "inline-block",
            background: "rgba(178,205,56,0.08)",
            border: "1px solid rgba(178,205,56,0.2)",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 10,
            color: "#B2CD38",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          {item.tag}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#fff",
            marginBottom: 10,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.75,
          }}
        >
          {item.desc}
        </div>
        {expanded && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.07)",
              fontSize: 13,
              lineHeight: 1.75,
            }}
          >
            <TypedText
              text={item.detail}
              style={{ color: "rgba(255,255,255,0.55)" }}
            />
          </div>
        )}
        <div
          style={{
            marginTop: 16,
            fontSize: 11,
            color: expanded ? "#B2CD38" : "rgba(255,255,255,0.2)",
            transition: "color 0.2s",
          }}
        >
          {expanded ? t.expandLess : t.expandMore}
        </div>
      </div>
    </FadeIn>
  );
}

function ProductCard({ item, index, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <FadeIn delay={index * 60}>
      <div style={{ height: "100%" }}>
        {/* Card content — split: top unblurred, bottom blurred */}
        {!item.ready ? (
          <div
            style={{
              background: "#162030",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 16,
              overflow: "hidden",
              height: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Top half — sharp */}
            <div style={{ padding: "28px 24px 20px", position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 70,
                  height: 70,
                  borderRadius: "0 0 0 70px",
                  background: item.color,
                  opacity: 0.07,
                }}
              />
              <div
                style={{
                  display: "inline-block",
                  background: `${item.color}18`,
                  border: `1px solid ${item.color}40`,
                  borderRadius: 6,
                  padding: "3px 10px",
                  fontSize: 10,
                  color: item.color,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                {item.tag}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: "#fff",
                  marginBottom: 8,
                }}
              >
                {item.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.45)",
                  lineHeight: 1.7,
                }}
              >
                {item.desc.slice(0, 60)}…
              </div>
            </div>

            {/* Divider */}
            <div
              style={{
                height: 1,
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
                margin: "0 20px",
              }}
            />

            {/* Bottom half — blurred + Coming Soon badge */}
            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              {/* Blurred content behind */}
              <div
                style={{
                  padding: "18px 24px",
                  filter: "blur(4px)",
                  opacity: 0.4,
                  userSelect: "none",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.7,
                    marginBottom: 14,
                  }}
                >
                  {item.desc.slice(60)}
                </div>
                <div style={{ fontSize: 11, color: item.color, opacity: 0.7 }}>
                  {t.poweredBy} →
                </div>
              </div>
              {/* Overlay with Coming Soon */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(10,18,28,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    background: "rgba(178,205,56,0.08)",
                    border: "1px solid rgba(178,205,56,0.25)",
                    borderRadius: 100,
                    padding: "6px 16px",
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "#B2CD38",
                      display: "inline-block",
                      opacity: 0.85,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      color: "#B2CD38",
                      fontWeight: 600,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t.comingSoon}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            onClick={() => setExpanded(!expanded)}
            style={{
              background: expanded ? "#1a2840" : "#162030",
              border: `1px solid ${
                expanded ? `${item.color}50` : "rgba(255,255,255,0.07)"
              }`,
              borderRadius: 16,
              padding: "28px 24px",
              position: "relative",
              overflow: "hidden",
              transition: "border-color 0.3s",
              cursor: "pointer",
              height: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
            onMouseEnter={(e) => {
              if (!expanded)
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
            }}
            onMouseLeave={(e) => {
              if (!expanded)
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
            }}
          >
            {expanded && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, transparent, ${item.color}, transparent)`,
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 80,
                height: 80,
                borderRadius: "0 0 0 80px",
                background: item.color,
                opacity: 0.07,
              }}
            />
            <div
              style={{
                display: "inline-block",
                background: `${item.color}18`,
                border: `1px solid ${item.color}40`,
                borderRadius: 6,
                padding: "3px 10px",
                fontSize: 10,
                color: item.color,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              {item.tag}
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: "#fff",
                marginBottom: 10,
              }}
            >
              {item.title}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.75,
                flex: 1,
              }}
            >
              {item.desc}
            </div>
            {expanded && item.detail && (
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: "1px solid rgba(255,255,255,0.07)",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.45)",
                  lineHeight: 1.75,
                }}
              >
                {item.detail}
              </div>
            )}
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: item.color,
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: 0.75,
              }}
            >
              <span>{t.poweredBy}</span>
              <span style={{ fontSize: 10 }}>→</span>
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}

function ChatWidget({ lang }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const initMsg =
    lang === "AZ"
      ? "Salam — mən Kibrit AI-yəm. Həllər, məhsullar haqqında soruşun və ya demo sifariş edin."
      : "Hello — I'm Kibrit AI. Ask me about our solutions, products, or request a demo.";
  const [messages, setMessages] = useState([{ from: "ai", text: initMsg }]);

  useEffect(() => {
    setMessages([{ from: "ai", text: initMsg }]);
  }, [lang]);
  const [input, setInput] = useState("");
  const [phone, setPhone] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [callSent, setCallSent] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;
    const userMsg = { from: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsSending(true);
    try {
      const history = [...messages, userMsg];
      const apiMessages = history.map((m) => ({
        role: m.from === "user" ? "user" : "assistant",
        content: m.text,
      }));
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 300,
          system: KIBRIT_SYSTEM,
          messages: apiMessages,
        }),
      });
      const data = await res.json();
      const reply = data?.content?.[0]?.text || fallbackChatReply(trimmed);
      setMessages((prev) => [...prev, { from: "ai", text: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { from: "ai", text: fallbackChatReply(trimmed) },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const requestCall = async () => {
    const trimmed = phone.trim();
    if (!trimmed || isCalling) return;
    setIsCalling(true);

    window.location.href = `mailto:hello@kibrit.tech?subject=${encodeURIComponent(
      "Kibrit AI — Call Request"
    )}&body=${encodeURIComponent(`Please call me at: ${trimmed}`)}`;

    try {
      const fd = new FormData();
      fd.append("type", "Call Request");
      fd.append("phone", trimmed);
      fd.append("_subject", "Kibrit AI — Call Request");
      await fetch(FORMSPREE_CALL, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
    } catch {}

    setCallSent(true);
    setPhone("");
    setIsCalling(false);
  };

  const quickPrompts =
    lang === "AZ"
      ? [
          "AI Core nədir?",
          "AzGuard AI haqqında",
          "Əlaqə Mərkəzi həlli necə işləyir?",
        ]
      : [
          "What is AI Core?",
          "Tell me about AzGuard AI",
          "How does AI Contact Center work?",
        ];

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            background: "#B2CD38",
            color: "#000",
            border: "none",
            borderRadius: 50,
            width: 56,
            height: 56,
            fontSize: 22,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 0 0 4px rgba(34,197,94,0.15), 0 4px 20px rgba(34,197,94,0.3)",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.08)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          aria-label="Open chat"
        >
          ✦
        </button>
      ) : (
        <div
          style={{
            width: 360,
            height: 580,
            background: "#162030",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 20,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#1a2840",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Logo height={22} />
              <div
                style={{
                  fontSize: 10,
                  color: "#B2CD38",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    background: "#B2CD38",
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                />
                Online
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setTab(tab === "chat" ? "call" : "chat")}
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 11,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                {tab === "chat"
                  ? lang === "AZ"
                    ? "📞 Zəng"
                    : "📞 Call"
                  : lang === "AZ"
                  ? "💬 Çat"
                  : "💬 Chat"}
              </button>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(255,255,255,0.4)",
                  fontSize: 18,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>

          {tab === "chat" ? (
            <>
              {/* Messages */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent:
                        msg.from === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "82%",
                        background: msg.from === "user" ? "#B2CD38" : "#1e3048",
                        color:
                          msg.from === "user"
                            ? "#000"
                            : "rgba(255,255,255,0.85)",
                        borderRadius:
                          msg.from === "user"
                            ? "16px 16px 4px 16px"
                            : "16px 16px 16px 4px",
                        padding: "10px 14px",
                        fontSize: 13,
                        lineHeight: 1.7,
                        border:
                          msg.from === "ai"
                            ? "1px solid rgba(255,255,255,0.07)"
                            : "none",
                      }}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      padding: "10px 14px",
                      background: "#1e3048",
                      borderRadius: "16px 16px 16px 4px",
                      width: "fit-content",
                      border: "1px solid rgba(255,255,255,0.07)",
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        style={{
                          width: 6,
                          height: 6,
                          background: "#B2CD38",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: `pulse 1.2s ease-in-out ${
                            i * 0.2
                          }s infinite`,
                        }}
                      />
                    ))}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick prompts */}
              {messages.length <= 1 && (
                <div
                  style={{
                    padding: "0 16px 12px",
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {quickPrompts.map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        setInput(p);
                      }}
                      style={{
                        background: "rgba(34,197,94,0.08)",
                        border: "1px solid rgba(34,197,94,0.2)",
                        borderRadius: 20,
                        color: "#B2CD38",
                        fontSize: 11,
                        padding: "5px 12px",
                        cursor: "pointer",
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div
                style={{
                  padding: "12px 14px",
                  borderTop: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  gap: 8,
                }}
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder={
                    lang === "AZ" ? "Sualınızı yazın..." : "Ask anything..."
                  }
                  style={{
                    flex: 1,
                    background: "#1e3048",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 10,
                    padding: "9px 14px",
                    color: "#fff",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={isSending || !input.trim()}
                  style={{
                    background: "#B2CD38",
                    border: "none",
                    borderRadius: 10,
                    color: "#000",
                    width: 38,
                    height: 38,
                    fontSize: 16,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: !input.trim() || isSending ? 0.4 : 1,
                    transition: "opacity 0.2s, transform 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (input.trim())
                      e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  →
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                padding: 24,
                gap: 12,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: "#fff",
                    marginBottom: 6,
                  }}
                >
                  {lang === "AZ" ? "Birbaşa əlaqə" : "Contact us directly"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.45)",
                    lineHeight: 1.7,
                  }}
                >
                  {lang === "AZ"
                    ? "Yazın — dərhal cavablayırıq."
                    : "Write to us — we reply fast."}
                </div>
              </div>

              {/* WhatsApp */}
              <a
                href="https://wa.me/994553550434?text=Salam%2C%20Kibrit%20AI%20haqqında%20məlumat%20almaq%20istəyirəm"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: "#1e3048",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "16px",
                  textDecoration: "none",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(37,211,102,0.4)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")
                }
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: "rgba(37,211,102,0.12)",
                    border: "1px solid rgba(37,211,102,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="#25D366"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#fff",
                      marginBottom: 3,
                    }}
                  >
                    WhatsApp
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                    +994 55 355 04 34
                  </div>
                </div>
                <div
                  style={{
                    marginLeft: "auto",
                    color: "rgba(255,255,255,0.25)",
                    fontSize: 18,
                  }}
                >
                  →
                </div>
              </a>

              {/* Telegram */}
              <a
                href="https://t.me/+994553550434"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: "#1e3048",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "16px",
                  textDecoration: "none",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(41,182,246,0.4)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")
                }
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: "rgba(41,182,246,0.12)",
                    border: "1px solid rgba(41,182,246,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="#29B6F6"
                  >
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#fff",
                      marginBottom: 3,
                    }}
                  >
                    Telegram
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                    +994 55 355 04 34
                  </div>
                </div>
                <div
                  style={{
                    marginLeft: "auto",
                    color: "rgba(255,255,255,0.25)",
                    fontSize: 18,
                  }}
                >
                  →
                </div>
              </a>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "rgba(255,255,255,0.2)",
                  textAlign: "center",
                }}
              >
                {lang === "AZ"
                  ? "Bir iş günü ərzində cavablayırıq"
                  : "We respond within one business day"}
              </div>
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
    </div>
  );
}

const translations = {
  EN: {
    nav: ["Home", "About", "Solutions", "Core", "Products", "Contact"],
    badge: "Kibrit AI — 2026",
    heroTitle1: "AI Layer for",
    heroTitle2: "Modern Systems",
    heroSub:
      "Kibrit transforms software into AI-powered systems that understand, decide, and act — powered by a unified intelligence layer built for enterprise scale.",
    btnSolutions: "Explore Solutions",
    btnArchitecture: "View Architecture",
    heroCardLabel: "Intelligence embedded",
    heroCardTitle: "Operations that think",
    heroCardSub: "Customer operations · Automation · Security",
    aboutLabel: "00 — About us",
    aboutTitle1: "We build",
    aboutTitle2: "intelligent systems",
    aboutTitleGreen: "intelligent systems",
    aboutP1:
      "Kibrit is a technology company focused on building intelligent systems for modern businesses. With over two decades of experience, we design and deliver software, automation, and digital solutions that support critical operations across industries.",
    aboutP2:
      "We operate at the intersection of engineering and innovation — developing both custom enterprise solutions and proprietary products. Our expertise spans customer operations, industrial automation, digital platforms, and large-scale system integration.",
    aboutP3:
      "Today, Kibrit is entering a new phase — where artificial intelligence becomes a core layer across everything we build, enabling systems that not only operate, but understand, decide, and act.",
    aboutCards: [
      {
        title: "Technology",
        text: "We build on our own AI Core architecture — a unified layer of understanding, reasoning, and execution that scales to any operational challenge.",
      },
      {
        title: "Approach",
        text: "We don't replace people — we amplify them. AI embeds into workflows as an intelligent partner that handles routine work so teams can focus on what matters.",
      },
      {
        title: "Market",
        text: "We serve the enterprise segment across Azerbaijan and the CIS, delivering enterprise-grade solutions with local expertise and dedicated support.",
      },
    ],
    coreLabel: "01 — Architecture",
    coreTitle1: "AI Core",
    coreTitle2: "Architecture",
    coreSub:
      "A centralized intelligence layer that orchestrates understanding, reasoning, and execution across all connected systems.",
    solutionsLabel: "02 — Solutions",
    solutionsTitle1: "Enterprise",
    solutionsTitle2: "Solutions",
    solutionsSub:
      "Purpose-built AI deployments for the operational challenges that matter most at scale.",
    productsLabel: "03 — Products",
    productsTitle1: "Our",
    productsTitle2: "Products",
    productsSub:
      "Consumer and enterprise products built on AI Core, each designed for a specific domain and use case.",
    poweredBy: "Powered by AI Core",
    comingSoon: "Coming Soon",
    contactLabel: "05 — Contact",
    contactTitle: "Get in touch",
    contactSub:
      "Ready to bring AI intelligence to your operations? Let's talk.",
    contactLocation: "Location",
    contactName: "Your name",
    contactEmail: "Your email",
    contactMsg: "Your message",
    contactSend: "Send message",
    contactSent: "Message sent — we'll be in touch shortly.",
    footerTagline: "AI-powered intelligent systems",
    expandMore: "▼ more",
    expandLess: "▲ less",
  },
  AZ: {
    nav: [
      "Ana Səhifə",
      "Haqqımızda",
      "Həllər",
      "AI Core",
      "Məhsullar",
      "Əlaqə",
    ],
    badge: "Kibrit AI — 2026",
    heroTitle1: "Müasir Sistemlər üçün",
    heroTitle2: "AI Platforması",
    heroSub:
      "Kibrit proqramlarınızı düşünən, qərar verən və müstəqil hərəkət edən sistemlərə çevirir — bütün müəssisə üçün vahid süni intellekt qatı əsasında.",
    btnSolutions: "Həllərə baxın",
    btnArchitecture: "Arxitekturaya baxın",
    heroCardLabel: "Sistemə qoşulmuş intellekt",
    heroCardTitle: "Düşünən iş prosesləri",
    heroCardSub: "Müştəri xidməti · Avtomatlaşdırma · Təhlükəsizlik",
    aboutLabel: "00 — Haqqımızda",
    aboutTitle1: "Biz İntellektual Sistemlər",
    aboutTitle2: "qururuq",
    aboutTitleGreen: "İntellektual Sistemlər",
    aboutP1:
      "Kibrit müasir şirkətlər üçün ağıllı sistemlər yaradan texnologiya şirkətidir. İyirmi ildən artıq təcrübəmizlə müxtəlif sahələrdəki kritik əməliyyatları dəstəkləyən proqram, avtomatlaşdırma və rəqəmsal həllər hazırlayırıq.",
    aboutP2:
      "Biz mühəndislik ilə innovasiyanın kəsişməsindəyik — həm fərdi korporativ həllər, həm də öz məhsullarımızı inkişaf etdiririk. Müştəri xidmətlərindən sənaye avtomatlaşdırmasına, rəqəmsal platformalardan böyük miqyaslı sistem inteqrasiyasına qədər geniş sahəni əhatə edirik.",
    aboutP3:
      "İndi Kibrit yeni bir mərhələyə qədəm qoyur — süni intellektin hər şeyin özəlinə çevrildiyi, sistemlərin yalnız işləməklə qalmayıb anladığı, qərar verdiyi və hərəkət etdiyi bir dövr.",
    aboutCards: [
      {
        title: "Texnologiya",
        text: "Öz AI Core arxitekturamız üzərində qururuq — anlama, mühakimə və icradan ibarət vahid qat hər ölçüdə korporativ problemləri həll edir.",
      },
      {
        title: "Yanaşma",
        text: "İnsanları əvəz etmirik — onları gücləndiririk. Süni intellekt iş axınlarına ağıllı tərəfdaş kimi qoşulur, rutin işləri avtomatik öz üzərinə götürür.",
      },
      {
        title: "Bazar",
        text: "Azərbaycan və MDB ölkələrindəki korporativ seqmentə xidmət edirik — yerli bilik və dəstəklə dünya standartlı həllər təqdim edirik.",
      },
    ],
    coreLabel: "01 — Arxitektura",
    coreTitle1: "AI Core",
    coreTitle2: "Arxitekturası",
    coreSub:
      "Bağlı bütün sistemlər arasında anlama, mühakimə və icraya rəhbərlik edən mərkəzləşdirilmiş intellekt qatı.",
    solutionsLabel: "02 — Həllər",
    solutionsTitle1: "Korporativ",
    solutionsTitle2: "İntellektual Həllər",
    solutionsSub:
      "Miqyaslı müəssisələrin ən kritik problemlərini həll etmək üçün xüsusi hazırlanmış AI tətbiqləri.",
    productsLabel: "03 — Məhsullar",
    productsTitle1: "Bizim",
    productsTitle2: "Məhsullarımız",
    productsSub:
      "AI Core üzərində qurulan istehlakçı və korporativ məhsullar — hər biri özünəməxsus bir sahə üçün.",
    poweredBy: "AI Core ilə işləyir",
    comingSoon: "Tezliklə",
    contactLabel: "05 — Əlaqə",
    contactTitle: "Bizimlə əlaqə",
    contactSub:
      "Sistemlərinizə süni intellekt gücü qatmağa hazırsınız? Əlaqə saxlayaq.",
    contactLocation: "Ünvan",
    contactName: "Adınız",
    contactEmail: "E-poçtunuz",
    contactMsg: "Mesajınız",
    contactSend: "Göndər",
    contactSent: "Mesajınız göndərildi — tezliklə sizinlə əlaqə saxlayacağıq.",
    footerTagline: "Süni intellektlə işləyən ağıllı sistemlər",
    expandMore: "▼ ətraflı",
    expandLess: "▲ bağla",
  },
};

const sectionIds = ["", "about", "solutions", "core", "products", "contact"];

const heroConversations = {
  EN: [
    {
      q: "What does Kibrit AI do?",
      a: "Kibrit AI builds an intelligent layer that embeds into your existing systems — enabling them to understand context, reason through decisions, and execute actions automatically across your operations.",
    },
    {
      q: "How does AI Core work?",
      a: "AI Core is a four-layer architecture: it understands input, integrates your live data, applies reasoning to generate the best action, and executes it across connected systems — all in real time.",
    },
    {
      q: "Which industries do you serve?",
      a: "We work across telecoms, finance, retail, and public sector — anywhere critical operations benefit from systems that think, not just process.",
    },
    {
      q: "Can I request a demo?",
      a: "Absolutely. Use the Contact section to send a message or request a callback — our team will reach out within one business day to walk you through a live session.",
    },
  ],
  AZ: [
    {
      q: "Kibrit AI nə ilə məşğuldur?",
      a: "Kibrit AI mövcud sistemlərinizə qoşulan intellektual qat qurur — onlar konteksti anlasın, qərar versin və əməliyyatlarınızda hərəkətləri avtomatik yerinə yetirsin deyə.",
    },
    {
      q: "AI Core necə işləyir?",
      a: "AI Core dörd qatdan ibarətdir: girişi anlayır, canlı məlumatlarınızı birləşdirir, ən düzgün addımı müəyyənləşdirir və onu bağlı sistemlər üzrə icra edir — hamısı real vaxtda.",
    },
    {
      q: "Hansı sahələrə xidmət edirsiniz?",
      a: "Telekommunikasiya, maliyyə, pərakəndə ticarət və dövlət sektoru üzrə çalışırıq — kritik əməliyyatların yalnız emal etməyən, düşünən sistemlərə ehtiyac duyduğu hər yerdə.",
    },
    {
      q: "Demo sifariş edə bilərəm?",
      a: "Əlbəttə. Əlaqə bölməsindən mesaj göndərin və ya geri zəng tələb edin — komandamız bir iş günü ərzində sizinlə əlaqə saxlayacaq.",
    },
  ],
};

function TypingHero({ lang }) {
  const convos = heroConversations[lang] || heroConversations.EN;
  const [convoIdx, setConvoIdx] = useState(0);
  const [phase, setPhase] = useState("typing-q"); // typing-q | pause-q | typing-a | pause-a | done
  const [displayed, setDisplayed] = useState("");
  const [charIdx, setCharIdx] = useState(0);
  const [history, setHistory] = useState([]);

  const current = convos[convoIdx];

  useEffect(() => {
    setHistory([]);
    setConvoIdx(0);
    setPhase("typing-q");
    setDisplayed("");
    setCharIdx(0);
  }, [lang]);

  useEffect(() => {
    let timeout;
    if (phase === "typing-q") {
      if (charIdx < current.q.length) {
        timeout = setTimeout(() => {
          setDisplayed(current.q.slice(0, charIdx + 1));
          setCharIdx((c) => c + 1);
        }, 38);
      } else {
        timeout = setTimeout(() => {
          setPhase("pause-q");
        }, 600);
      }
    } else if (phase === "pause-q") {
      setHistory((h) => [...h, { role: "user", text: current.q }]);
      setDisplayed("");
      setCharIdx(0);
      setPhase("typing-a");
    } else if (phase === "typing-a") {
      if (charIdx < current.a.length) {
        timeout = setTimeout(() => {
          setDisplayed(current.a.slice(0, charIdx + 1));
          setCharIdx((c) => c + 1);
        }, 18);
      } else {
        timeout = setTimeout(() => {
          setPhase("pause-a");
        }, 1800);
      }
    } else if (phase === "pause-a") {
      setHistory((h) => [...h, { role: "ai", text: current.a }]);
      setDisplayed("");
      setCharIdx(0);
      const next = (convoIdx + 1) % convos.length;
      setConvoIdx(next);
      setPhase("typing-q");
    }
    return () => clearTimeout(timeout);
  }, [phase, charIdx, current, convos.length]);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [history, displayed]);

  return (
    <div
      style={{
        borderRadius: 20,
        border: "1px solid rgba(178,205,56,0.18)",
        background: "rgba(10,18,30,0.92)",
        height: "100%",
        minHeight: 420,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(20,36,54,0.8)",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {["#EF4444", "#F59E0B", "#10B981"].map((c) => (
            <div
              key={c}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: c,
                opacity: 0.7,
              }}
            />
          ))}
        </div>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 11,
            color: "rgba(255,255,255,0.3)",
            letterSpacing: "0.1em",
          }}
        >
          kibrit.ai — intelligent assistant
        </div>
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#B2CD38",
          }}
        />
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 20px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          scrollbarWidth: "none",
        }}
      >
        {history.map((msg, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            {msg.role === "ai" && (
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "rgba(178,205,56,0.15)",
                  border: "1px solid rgba(178,205,56,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 8,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle
                    cx="6"
                    cy="6"
                    r="4"
                    stroke="#B2CD38"
                    strokeWidth="1.2"
                  />
                  <circle cx="6" cy="6" r="1.5" fill="#B2CD38" />
                </svg>
              </div>
            )}
            <div
              style={{
                maxWidth: "78%",
                background:
                  msg.role === "user"
                    ? "rgba(178,205,56,0.12)"
                    : "rgba(255,255,255,0.05)",
                border:
                  msg.role === "user"
                    ? "1px solid rgba(178,205,56,0.2)"
                    : "1px solid rgba(255,255,255,0.07)",
                borderRadius:
                  msg.role === "user"
                    ? "14px 14px 4px 14px"
                    : "14px 14px 14px 4px",
                padding: "10px 14px",
                fontSize: 13,
                color:
                  msg.role === "user" ? "#B2CD38" : "rgba(255,255,255,0.75)",
                lineHeight: 1.7,
              }}
            >
              {msg.text}
            </div>
          </div>
        ))}

        {/* Live typing bubble */}
        {displayed && (
          <div
            style={{
              display: "flex",
              justifyContent: phase === "typing-q" ? "flex-end" : "flex-start",
            }}
          >
            {phase === "typing-a" && (
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "rgba(178,205,56,0.15)",
                  border: "1px solid rgba(178,205,56,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 8,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle
                    cx="6"
                    cy="6"
                    r="4"
                    stroke="#B2CD38"
                    strokeWidth="1.2"
                  />
                  <circle cx="6" cy="6" r="1.5" fill="#B2CD38" />
                </svg>
              </div>
            )}
            <div
              style={{
                maxWidth: "78%",
                background:
                  phase === "typing-q"
                    ? "rgba(178,205,56,0.12)"
                    : "rgba(255,255,255,0.05)",
                border:
                  phase === "typing-q"
                    ? "1px solid rgba(178,205,56,0.2)"
                    : "1px solid rgba(255,255,255,0.07)",
                borderRadius:
                  phase === "typing-q"
                    ? "14px 14px 4px 14px"
                    : "14px 14px 14px 4px",
                padding: "10px 14px",
                fontSize: 13,
                color:
                  phase === "typing-q" ? "#B2CD38" : "rgba(255,255,255,0.75)",
                lineHeight: 1.7,
              }}
            >
              {displayed}
              <span
                style={{
                  display: "inline-block",
                  width: 2,
                  height: 13,
                  background:
                    phase === "typing-q" ? "#B2CD38" : "rgba(255,255,255,0.5)",
                  marginLeft: 2,
                  verticalAlign: "middle",
                  animation: "blink-cursor 0.8s step-end infinite",
                }}
              />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar (decorative) */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          gap: 10,
          alignItems: "center",
          background: "rgba(14,24,38,0.6)",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
            padding: "9px 14px",
            fontSize: 12,
            color: "rgba(255,255,255,0.2)",
            fontStyle: "italic",
          }}
        >
          {lang === "AZ" ? "Sualınızı yazın..." : "Ask anything..."}
        </div>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "#B2CD38",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 7h10M8 3l4 4-4 4"
              stroke="#1a2738"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      <style>{`
        @keyframes blink-cursor { 0%,100%{opacity:1} 50%{opacity:0} }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

function ParticleBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let W = window.innerWidth,
      H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;
    const onResize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    // --- Chaotic neurons ---
    const COUNT = 140,
      MAX_DIST = 180;
    const dots = Array.from({ length: COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 1.8,
      vy: (Math.random() - 0.5) * 1.8,
      ax: 0,
      ay: 0,
      r: Math.random() * 2 + 0.8,
      pulse: Math.random() * Math.PI * 2,
      dpulse: Math.random() * 0.04 + 0.01,
      alpha: 0.3,
    }));
    const signals = Array.from({ length: 20 }, () => {
      const f = Math.floor(Math.random() * COUNT);
      let t = Math.floor(Math.random() * COUNT);
      while (t === f) t = Math.floor(Math.random() * COUNT);
      return { f, t, pos: Math.random(), spd: 0.012 + Math.random() * 0.018 };
    });

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      dots.forEach((d) => {
        // Random acceleration — chaotic direction changes every frame
        d.ax = (Math.random() - 0.5) * 0.14;
        d.ay = (Math.random() - 0.5) * 0.14;
        d.vx = (d.vx + d.ax) * 0.978;
        d.vy = (d.vy + d.ay) * 0.978;

        // Speed clamp
        const spd = Math.sqrt(d.vx * d.vx + d.vy * d.vy);
        if (spd > 2.4) {
          d.vx *= 2.4 / spd;
          d.vy *= 2.4 / spd;
        }
        if (spd < 0.3) {
          d.vx += (Math.random() - 0.5) * 0.5;
          d.vy += (Math.random() - 0.5) * 0.5;
        }

        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > W) {
          d.vx *= -1;
          d.x = Math.max(0, Math.min(W, d.x));
        }
        if (d.y < 0 || d.y > H) {
          d.vy *= -1;
          d.y = Math.max(0, Math.min(H, d.y));
        }

        d.pulse += d.dpulse;
        d.alpha = 0.2 + Math.abs(Math.sin(d.pulse)) * 0.6;
      });

      // Connections
      for (let i = 0; i < COUNT; i++)
        for (let j = i + 1; j < COUNT; j++) {
          const dx = dots[i].x - dots[j].x,
            dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const r = 1 - dist / MAX_DIST;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.strokeStyle = `rgba(178,205,56,${r * 0.28})`;
            ctx.lineWidth = r * 0.8;
            ctx.stroke();
          }
        }

      // Signals
      signals.forEach((s) => {
        s.pos += s.spd;
        if (s.pos > 1) {
          s.pos = 0;
          s.f = Math.floor(Math.random() * COUNT);
          s.t = Math.floor(Math.random() * COUNT);
        }
        const dx = dots[s.t].x - dots[s.f].x,
          dy = dots[s.t].y - dots[s.f].y;
        if (Math.sqrt(dx * dx + dy * dy) < MAX_DIST) {
          ctx.beginPath();
          ctx.arc(
            dots[s.f].x + dx * s.pos,
            dots[s.f].y + dy * s.pos,
            2.5,
            0,
            Math.PI * 2
          );
          ctx.fillStyle = `rgba(212,240,74,${
            0.8 * (1 - Math.abs(s.pos - 0.5) * 1.8)
          })`;
          ctx.fill();
        }
      });

      // Dots
      dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(178,205,56,${d.alpha})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}

export default function KibritAI() {
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState("EN");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [contactSent, setContactSent] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const t = translations[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    if (!id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [contactSending, setContactSending] = useState(false);

  const submitContact = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Kibrit AI — ${contactForm.name}`);
    const body = encodeURIComponent(
      `Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`
    );
    window.location.href = `mailto:hello@kibrit.tech?subject=${subject}&body=${body}`;
    setContactSent(true);
    setContactForm({ name: "", email: "", message: "" });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#1a2738",
        color: "#fff",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        overflowX: "hidden",
        position: "relative",
        zIndex: 1,
      }}
    >
      <ParticleBackground />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=DM+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />

      {/* NAV */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderBottom:
            scrolled || menuOpen
              ? "1px solid rgba(255,255,255,0.07)"
              : "1px solid transparent",
          background:
            scrolled || menuOpen ? "rgba(26,39,56,0.97)" : "transparent",
          backdropFilter: scrolled || menuOpen ? "blur(12px)" : "none",
          transition: "all 0.4s ease",
          padding: isMobile ? "0 20px" : "0 40px",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 64,
          }}
        >
          {/* Logo */}
          <button
            onClick={() => {
              scrollTo("");
              setMenuOpen(false);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Logo height={isMobile ? 22 : 28} />
          </button>

          {/* Desktop nav links */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 32,
                fontSize: 13,
                color: "rgba(255,255,255,0.5)",
              }}
            >
              {t.nav.map((label, i) => {
                const id = sectionIds[i];
                const isContact = i === t.nav.length - 1;
                return (
                  <button
                    key={label}
                    onClick={() => scrollTo(id)}
                    style={{
                      background: isContact ? "#B2CD38" : "none",
                      border: "none",
                      borderRadius: isContact ? 8 : 0,
                      color: isContact ? "#1a2738" : "rgba(255,255,255,0.5)",
                      fontSize: 13,
                      fontWeight: isContact ? 600 : 400,
                      padding: isContact ? "7px 18px" : "0",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => {
                      if (!isContact) e.currentTarget.style.color = "#fff";
                      else e.currentTarget.style.opacity = "0.85";
                    }}
                    onMouseLeave={(e) => {
                      if (!isContact)
                        e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                      else e.currentTarget.style.opacity = "1";
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              <div
                style={{
                  display: "flex",
                  gap: 2,
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 8,
                  padding: "3px",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {["EN", "AZ"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    style={{
                      background: lang === l ? "#B2CD38" : "transparent",
                      color: lang === l ? "#1a2738" : "rgba(255,255,255,0.4)",
                      border: "none",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "inherit",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mobile: lang + hamburger */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  gap: 2,
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 8,
                  padding: "3px",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {["EN", "AZ"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    style={{
                      background: lang === l ? "#B2CD38" : "transparent",
                      color: lang === l ? "#1a2738" : "rgba(255,255,255,0.4)",
                      border: "none",
                      borderRadius: 6,
                      padding: "3px 8px",
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                }}
              >
                <span
                  style={{
                    display: "block",
                    width: 22,
                    height: 1.5,
                    background: "#fff",
                    transition: "all 0.3s",
                    transform: menuOpen
                      ? "rotate(45deg) translate(4.5px, 4.5px)"
                      : "none",
                  }}
                />
                <span
                  style={{
                    display: "block",
                    width: 22,
                    height: 1.5,
                    background: "#fff",
                    transition: "all 0.3s",
                    opacity: menuOpen ? 0 : 1,
                  }}
                />
                <span
                  style={{
                    display: "block",
                    width: 22,
                    height: 1.5,
                    background: "#fff",
                    transition: "all 0.3s",
                    transform: menuOpen
                      ? "rotate(-45deg) translate(4.5px, -4.5px)"
                      : "none",
                  }}
                />
              </button>
            </div>
          )}
        </div>

        {/* Mobile dropdown menu */}
        {isMobile && menuOpen && (
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.07)",
              padding: "16px 20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {t.nav.map((label, i) => {
              const isContact = i === t.nav.length - 1;
              return (
                <button
                  key={label}
                  onClick={() => {
                    scrollTo(sectionIds[i]);
                    setMenuOpen(false);
                  }}
                  style={{
                    background: isContact ? "#B2CD38" : "transparent",
                    border: "none",
                    borderRadius: isContact ? 10 : 0,
                    color: isContact ? "#1a2738" : "rgba(255,255,255,0.7)",
                    fontSize: 16,
                    fontWeight: isContact ? 600 : 400,
                    padding: isContact ? "12px 20px" : "10px 0",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    borderBottom: !isContact
                      ? "1px solid rgba(255,255,255,0.05)"
                      : "none",
                    marginTop: isContact ? 8 : 0,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: isMobile ? "100px 20px 60px" : "120px 40px 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 700,
            height: 700,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(178,205,56,0.08) 0%, rgba(178,205,56,0.03) 40%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(30,100,180,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(178,205,56,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(178,205,56,0.04) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        {!isMobile && (
          <svg
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 600,
              height: 600,
              pointerEvents: "none",
              opacity: 0.12,
            }}
            viewBox="0 0 600 600"
          >
            <line
              x1="600"
              y1="0"
              x2="200"
              y2="600"
              stroke="#B2CD38"
              strokeWidth="0.5"
            />
            <line
              x1="600"
              y1="80"
              x2="280"
              y2="600"
              stroke="#B2CD38"
              strokeWidth="0.5"
            />
            <line
              x1="520"
              y1="0"
              x2="120"
              y2="600"
              stroke="#B2CD38"
              strokeWidth="0.3"
            />
          </svg>
        )}
        <div
          style={{
            position: "absolute",
            top: "38%",
            left: 0,
            right: 0,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(178,205,56,0.15) 30%, rgba(178,205,56,0.25) 50%, rgba(178,205,56,0.15) 70%, transparent 100%)",
            pointerEvents: "none",
          }}
        />
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            opacity: 0.18,
          }}
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
        >
          {Array.from({ length: 8 }, (_, row) =>
            Array.from({ length: 14 }, (_, col) => (
              <circle
                key={`${row}-${col}`}
                cx={col * 90 + 45}
                cy={row * 110 + 55}
                r="1.2"
                fill="#B2CD38"
                opacity={(row + col) % 3 === 0 ? 0.8 : 0.3}
              />
            ))
          )}
        </svg>
        {!isMobile && (
          <>
            <svg
              style={{
                position: "absolute",
                top: 80,
                left: 30,
                pointerEvents: "none",
                opacity: 0.3,
              }}
              width="40"
              height="40"
              viewBox="0 0 40 40"
            >
              <path
                d="M0 20 L0 0 L20 0"
                stroke="#B2CD38"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            <svg
              style={{
                position: "absolute",
                bottom: 40,
                right: 30,
                pointerEvents: "none",
                opacity: 0.3,
              }}
              width="40"
              height="40"
              viewBox="0 0 40 40"
            >
              <path
                d="M40 20 L40 40 L20 40"
                stroke="#B2CD38"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
          </>
        )}

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 40 : 80,
            alignItems: "center",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div>
            <FadeIn delay={0}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(178,205,56,0.08)",
                  border: "1px solid rgba(178,205,56,0.2)",
                  borderRadius: 100,
                  padding: "6px 16px",
                  marginBottom: 28,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    background: "#B2CD38",
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: "#B2CD38",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                  }}
                >
                  {t.badge}
                </span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h1
                style={{
                  fontSize: isMobile ? 40 : 64,
                  fontWeight: 300,
                  lineHeight: 1.1,
                  marginBottom: 22,
                  letterSpacing: "-0.02em",
                }}
              >
                {t.heroTitle1}
                <br />
                <span style={{ fontWeight: 600, color: "#B2CD38" }}>
                  {t.heroTitle2}
                </span>
              </h1>
            </FadeIn>
            <FadeIn delay={200}>
              <p
                style={{
                  fontSize: isMobile ? 15 : 17,
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.8,
                  maxWidth: 480,
                  marginBottom: 32,
                }}
              >
                {t.heroSub}
              </p>
            </FadeIn>
            <FadeIn delay={300}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={() => scrollTo("solutions")}
                  style={{
                    background: "#B2CD38",
                    color: "#1a2738",
                    padding: isMobile ? "11px 22px" : "13px 28px",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {t.btnSolutions} →
                </button>
                <button
                  onClick={() => scrollTo("core")}
                  style={{
                    background: "transparent",
                    color: "rgba(255,255,255,0.6)",
                    padding: isMobile ? "11px 22px" : "13px 28px",
                    borderRadius: 10,
                    fontSize: 14,
                    border: "1px solid rgba(255,255,255,0.15)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#fff";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.15)";
                  }}
                >
                  {t.btnArchitecture}
                </button>
              </div>
            </FadeIn>
          </div>

          {/* Hero visual — animated AI network */}
          <FadeIn delay={200}>
            <div
              style={{
                position: "relative",
                borderRadius: 24,
                overflow: "hidden",
                border: "1px solid rgba(178,205,56,0.2)",
                aspectRatio: "4/3",
                background: "#0e1c2e",
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 520 390"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block" }}
              >
                <defs>
                  <radialGradient id="bgGlow" cx="60%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#1a3a5c" stopOpacity="1" />
                    <stop offset="100%" stopColor="#0a1520" stopOpacity="1" />
                  </radialGradient>
                  <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#B2CD38" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#B2CD38" stopOpacity="0" />
                  </radialGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <style>{`
                    @keyframes pulse1{0%,100%{opacity:.3;r:5}50%{opacity:1;r:8}}
                    @keyframes pulse2{0%,100%{opacity:.2;r:4}50%{opacity:.9;r:7}}
                    @keyframes pulse3{0%,100%{opacity:.4;r:6}50%{opacity:1;r:9}}
                    @keyframes scan{0%{transform:translateY(-10px);opacity:0}10%{opacity:1}90%{opacity:1}100%{transform:translateY(390px);opacity:0}}
                    @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
                    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
                    .n1{animation:pulse1 2.4s ease-in-out infinite}
                    .n2{animation:pulse2 2.1s ease-in-out infinite .4s}
                    .n3{animation:pulse3 2.8s ease-in-out infinite .8s}
                    .n4{animation:pulse1 2.2s ease-in-out infinite 1.2s}
                    .n5{animation:pulse2 2.6s ease-in-out infinite .6s}
                    .n6{animation:pulse3 2.0s ease-in-out infinite 1s}
                    .n7{animation:pulse1 2.5s ease-in-out infinite 1.4s}
                    .n8{animation:pulse2 2.3s ease-in-out infinite .2s}
                    .n9{animation:pulse3 2.7s ease-in-out infinite 1.6s}
                    .scanline{animation:scan 4s linear infinite}
                    .blink{animation:blink 1.2s ease-in-out infinite}
                    .float{animation:float 4s ease-in-out infinite}
                  `}</style>
                </defs>

                <rect width="520" height="390" fill="url(#bgGlow)" />

                {/* Grid */}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <line
                    key={`h${i}`}
                    x1="0"
                    y1={i * 52}
                    x2="520"
                    y2={i * 52}
                    stroke="#B2CD38"
                    strokeWidth="0.3"
                    opacity="0.08"
                  />
                ))}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <line
                    key={`v${i}`}
                    x1={i * 52}
                    y1="0"
                    x2={i * 52}
                    y2="390"
                    stroke="#B2CD38"
                    strokeWidth="0.3"
                    opacity="0.08"
                  />
                ))}

                {/* Scan line */}
                <line
                  className="scanline"
                  x1="0"
                  y1="0"
                  x2="520"
                  y2="0"
                  stroke="#B2CD38"
                  strokeWidth="1"
                  opacity="0.25"
                />

                {/* ── Static edges — no dashes, just faint lines ── */}
                {/* Layer 0→1 */}
                <line
                  x1="80"
                  y1="110"
                  x2="200"
                  y2="80"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                <line
                  x1="80"
                  y1="110"
                  x2="200"
                  y2="160"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.2"
                />
                <line
                  x1="80"
                  y1="195"
                  x2="200"
                  y2="80"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.18"
                />
                <line
                  x1="80"
                  y1="195"
                  x2="200"
                  y2="160"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                <line
                  x1="80"
                  y1="195"
                  x2="200"
                  y2="240"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.2"
                />
                <line
                  x1="80"
                  y1="280"
                  x2="200"
                  y2="160"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.18"
                />
                <line
                  x1="80"
                  y1="280"
                  x2="200"
                  y2="240"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                {/* Layer 1→2 */}
                <line
                  x1="200"
                  y1="80"
                  x2="320"
                  y2="120"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.22"
                />
                <line
                  x1="200"
                  y1="80"
                  x2="320"
                  y2="200"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.18"
                />
                <line
                  x1="200"
                  y1="160"
                  x2="320"
                  y2="120"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                <line
                  x1="200"
                  y1="160"
                  x2="320"
                  y2="200"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.2"
                />
                <line
                  x1="200"
                  y1="160"
                  x2="320"
                  y2="280"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.18"
                />
                <line
                  x1="200"
                  y1="240"
                  x2="320"
                  y2="200"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.22"
                />
                <line
                  x1="200"
                  y1="240"
                  x2="320"
                  y2="280"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                {/* Layer 2→3 */}
                <line
                  x1="320"
                  y1="120"
                  x2="440"
                  y2="195"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.25"
                />
                <line
                  x1="320"
                  y1="200"
                  x2="440"
                  y2="195"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.28"
                />
                <line
                  x1="320"
                  y1="280"
                  x2="440"
                  y2="195"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.22"
                />

                {/* ── Travelling signal dots via animateMotion ── */}
                {/* Each dot follows a path from Input node → through hidden layers → Output */}

                {/* Signal 1: (80,110)→(200,80)→(320,120)→(440,195) */}
                <circle r="3" fill="#d4f04a" filter="url(#glow)" opacity="0.9">
                  <animateMotion dur="2.8s" repeatCount="indefinite" begin="0s">
                    <mpath href="#path-s1" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="2.8s"
                    repeatCount="indefinite"
                    begin="0s"
                  />
                </circle>
                <path
                  id="path-s1"
                  d="M80,110 L200,80 L320,120 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* Signal 2: (80,195)→(200,160)→(320,200)→(440,195) */}
                <circle r="3" fill="#B2CD38" filter="url(#glow)" opacity="0.9">
                  <animateMotion
                    dur="3.1s"
                    repeatCount="indefinite"
                    begin="0.6s"
                  >
                    <mpath href="#path-s2" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="3.1s"
                    repeatCount="indefinite"
                    begin="0.6s"
                  />
                </circle>
                <path
                  id="path-s2"
                  d="M80,195 L200,160 L320,200 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* Signal 3: (80,280)→(200,240)→(320,280)→(440,195) */}
                <circle r="3" fill="#d4f04a" filter="url(#glow)" opacity="0.9">
                  <animateMotion
                    dur="3.4s"
                    repeatCount="indefinite"
                    begin="1.2s"
                  >
                    <mpath href="#path-s3" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="3.4s"
                    repeatCount="indefinite"
                    begin="1.2s"
                  />
                </circle>
                <path
                  id="path-s3"
                  d="M80,280 L200,240 L320,280 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* Signal 4: (80,110)→(200,160)→(320,120)→(440,195) */}
                <circle
                  r="2.5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                  opacity="0.8"
                >
                  <animateMotion
                    dur="2.6s"
                    repeatCount="indefinite"
                    begin="1.8s"
                  >
                    <mpath href="#path-s4" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="2.6s"
                    repeatCount="indefinite"
                    begin="1.8s"
                  />
                </circle>
                <path
                  id="path-s4"
                  d="M80,110 L200,160 L320,120 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* Signal 5: (80,195)→(200,80)→(320,200)→(440,195) */}
                <circle
                  r="2.5"
                  fill="#d4f04a"
                  filter="url(#glow)"
                  opacity="0.8"
                >
                  <animateMotion
                    dur="3.0s"
                    repeatCount="indefinite"
                    begin="0.3s"
                  >
                    <mpath href="#path-s5" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="3.0s"
                    repeatCount="indefinite"
                    begin="0.3s"
                  />
                </circle>
                <path
                  id="path-s5"
                  d="M80,195 L200,80 L320,200 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* Signal 6: (80,280)→(200,160)→(320,280)→(440,195) */}
                <circle
                  r="2.5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                  opacity="0.8"
                >
                  <animateMotion
                    dur="3.3s"
                    repeatCount="indefinite"
                    begin="2.1s"
                  >
                    <mpath href="#path-s6" />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;1;0"
                    keyTimes="0;0.05;0.5;0.95;1"
                    dur="3.3s"
                    repeatCount="indefinite"
                    begin="2.1s"
                  />
                </circle>
                <path
                  id="path-s6"
                  d="M80,280 L200,160 L320,280 L440,195"
                  fill="none"
                  visibility="hidden"
                />

                {/* ── Node glows ── */}
                <circle
                  cx="80"
                  cy="110"
                  r="20"
                  fill="url(#nodeGlow)"
                  opacity="0.6"
                />
                <circle
                  cx="80"
                  cy="195"
                  r="22"
                  fill="url(#nodeGlow)"
                  opacity="0.8"
                />
                <circle
                  cx="80"
                  cy="280"
                  r="18"
                  fill="url(#nodeGlow)"
                  opacity="0.5"
                />
                <circle
                  cx="200"
                  cy="80"
                  r="18"
                  fill="url(#nodeGlow)"
                  opacity="0.6"
                />
                <circle
                  cx="200"
                  cy="160"
                  r="24"
                  fill="url(#nodeGlow)"
                  opacity="0.9"
                />
                <circle
                  cx="200"
                  cy="240"
                  r="18"
                  fill="url(#nodeGlow)"
                  opacity="0.6"
                />
                <circle
                  cx="320"
                  cy="120"
                  r="20"
                  fill="url(#nodeGlow)"
                  opacity="0.7"
                />
                <circle
                  cx="320"
                  cy="200"
                  r="22"
                  fill="url(#nodeGlow)"
                  opacity="0.8"
                />
                <circle
                  cx="320"
                  cy="280"
                  r="18"
                  fill="url(#nodeGlow)"
                  opacity="0.5"
                />
                <circle
                  cx="440"
                  cy="195"
                  r="26"
                  fill="url(#nodeGlow)"
                  opacity="1"
                />
                <circle
                  cx="80"
                  cy="110"
                  r="10"
                  fill="none"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.3"
                />
                <circle
                  cx="80"
                  cy="195"
                  r="10"
                  fill="none"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.3"
                />
                <circle
                  cx="80"
                  cy="280"
                  r="10"
                  fill="none"
                  stroke="#B2CD38"
                  strokeWidth="0.8"
                  opacity="0.3"
                />
                <circle
                  cx="440"
                  cy="195"
                  r="14"
                  fill="none"
                  stroke="#B2CD38"
                  strokeWidth="1"
                  opacity="0.4"
                />

                {/* ── Pulsing node cores ── */}
                <circle
                  className="n1"
                  cx="80"
                  cy="110"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n2"
                  cx="80"
                  cy="195"
                  r="6"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n3"
                  cx="80"
                  cy="280"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n4"
                  cx="200"
                  cy="80"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n5"
                  cx="200"
                  cy="160"
                  r="6"
                  fill="#d4f04a"
                  filter="url(#glow)"
                />
                <circle
                  className="n6"
                  cx="200"
                  cy="240"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n7"
                  cx="320"
                  cy="120"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n8"
                  cx="320"
                  cy="200"
                  r="6"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n9"
                  cx="320"
                  cy="280"
                  r="5"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  className="n3"
                  cx="440"
                  cy="195"
                  r="9"
                  fill="#B2CD38"
                  filter="url(#glow)"
                />
                <circle
                  cx="440"
                  cy="195"
                  r="9"
                  fill="none"
                  stroke="#d4f04a"
                  strokeWidth="1.5"
                  opacity="0.6"
                />

                {/* Labels */}
                <text
                  x="80"
                  y="316"
                  textAnchor="middle"
                  fill="#B2CD38"
                  fontSize="9"
                  opacity="0.4"
                  fontFamily="monospace"
                  letterSpacing="1"
                >
                  INPUT
                </text>
                <text
                  x="200"
                  y="276"
                  textAnchor="middle"
                  fill="#B2CD38"
                  fontSize="9"
                  opacity="0.4"
                  fontFamily="monospace"
                  letterSpacing="1"
                >
                  HIDDEN
                </text>
                <text
                  x="320"
                  y="316"
                  textAnchor="middle"
                  fill="#B2CD38"
                  fontSize="9"
                  opacity="0.4"
                  fontFamily="monospace"
                  letterSpacing="1"
                >
                  HIDDEN
                </text>
                <text
                  x="440"
                  y="232"
                  textAnchor="middle"
                  fill="#B2CD38"
                  fontSize="9"
                  opacity="0.4"
                  fontFamily="monospace"
                  letterSpacing="1"
                >
                  OUTPUT
                </text>

                {/* Floating status card */}
                <g className="float">
                  <rect
                    x="20"
                    y="20"
                    width="130"
                    height="46"
                    rx="6"
                    fill="#0e1c2e"
                    stroke="#B2CD38"
                    strokeWidth="0.6"
                    opacity="0.8"
                  />
                  <text
                    x="32"
                    y="38"
                    fill="#B2CD38"
                    fontSize="8"
                    fontFamily="monospace"
                    opacity="0.5"
                    letterSpacing="1"
                  >
                    KIBRIT AI CORE
                  </text>
                  <text
                    x="32"
                    y="54"
                    fill="#ffffff"
                    fontSize="10"
                    fontFamily="monospace"
                    fontWeight="bold"
                  >
                    ACTIVE
                  </text>
                  <circle
                    className="blink"
                    cx="110"
                    cy="50"
                    r="3.5"
                    fill="#B2CD38"
                  />
                </g>

                <text
                  x="500"
                  y="370"
                  textAnchor="end"
                  fill="#B2CD38"
                  fontSize="8"
                  fontFamily="monospace"
                  opacity="0.3"
                  letterSpacing="1"
                >
                  SYS/02
                </text>
                <text
                  x="500"
                  y="382"
                  textAnchor="end"
                  fill="#B2CD38"
                  fontSize="8"
                  fontFamily="monospace"
                  opacity="0.3"
                  letterSpacing="1"
                >
                  v2.0.26
                </text>

                {/* Bottom info panel */}
                <rect
                  x="0"
                  y="320"
                  width="520"
                  height="70"
                  fill="url(#bgGlow)"
                  opacity="0.7"
                />
                <rect
                  x="0"
                  y="320"
                  width="520"
                  height="1"
                  fill="#B2CD38"
                  opacity="0.15"
                />
                <text
                  x="28"
                  y="342"
                  fill="#B2CD38"
                  fontSize="8"
                  fontFamily="monospace"
                  opacity="0.45"
                  letterSpacing="2"
                >
                  {lang === "AZ"
                    ? "DAXILƏ YERLƏŞDİRİLMİŞ İNTELLEKT"
                    : "INTELLIGENCE EMBEDDED"}
                </text>
                <text
                  x="28"
                  y="360"
                  fill="#ffffff"
                  fontSize="14"
                  fontFamily="sans-serif"
                  fontWeight="500"
                >
                  {lang === "AZ"
                    ? "Düşünən əməliyyatlar"
                    : "Operations that think"}
                </text>
                <text
                  x="28"
                  y="376"
                  fill="rgba(255,255,255,0.45)"
                  fontSize="10"
                  fontFamily="sans-serif"
                >
                  {lang === "AZ"
                    ? "Müştəri əməliyyatları · Avtomatlaşdırma · Təhlükəsizlik"
                    : "Customer ops · Automation · Security"}
                </text>
              </svg>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        style={{
          background: "#152030",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: isMobile ? "60px 20px" : "100px 40px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 40 : 80,
              alignItems: "center",
            }}
          >
            {/* Left — text */}
            <div>
              <FadeIn>
                <div
                  style={{
                    fontSize: 11,
                    color: "#B2CD38",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    marginBottom: 16,
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {t.aboutLabel}
                </div>
                <h2
                  style={{
                    fontSize: isMobile ? 28 : 42,
                    fontWeight: 300,
                    letterSpacing: "-0.02em",
                    marginBottom: 20,
                    lineHeight: 1.2,
                  }}
                >
                  {lang === "EN" ? (
                    <>
                      {t.aboutTitle1}
                      <br />
                      <span style={{ fontWeight: 600, color: "#B2CD38" }}>
                        {t.aboutTitle2}
                      </span>
                    </>
                  ) : (
                    <>
                      Biz{" "}
                      <span style={{ fontWeight: 600, color: "#B2CD38" }}>
                        İntellektual Sistemlər
                      </span>
                      <br />
                      qururuq
                    </>
                  )}
                </h2>
                <p
                  style={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.6)",
                    lineHeight: 1.9,
                    marginBottom: 20,
                  }}
                >
                  {t.aboutP1}
                </p>
                <p
                  style={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.6)",
                    lineHeight: 1.9,
                    marginBottom: 20,
                  }}
                >
                  {t.aboutP2}
                </p>
                <p
                  style={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.6)",
                    lineHeight: 1.9,
                  }}
                >
                  {t.aboutP3}
                </p>
              </FadeIn>
            </div>

            {/* Right — cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.aboutCards.map((item, i) => (
                <FadeIn key={item.title} delay={i * 100}>
                  <div
                    style={{
                      background: "#1a2738",
                      border: "1px solid rgba(255,255,255,0.07)",
                      borderRadius: 14,
                      padding: "22px 24px",
                      display: "flex",
                      gap: 18,
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        color: "#B2CD38",
                        fontFamily: "monospace",
                        lineHeight: 1,
                        marginTop: 2,
                        flexShrink: 0,
                      }}
                    >
                      {["◎", "⊗", "◈"][i]}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#fff",
                          marginBottom: 8,
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.5)",
                          lineHeight: 1.75,
                        }}
                      >
                        {item.text}
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CORE */}
      <section
        id="core"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: isMobile ? "60px 20px" : "100px 40px",
        }}
      >
        <FadeIn>
          <div style={{ marginBottom: 60 }}>
            <div
              style={{
                fontSize: 11,
                color: "#B2CD38",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                marginBottom: 16,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {t.coreLabel}
            </div>
            <h2
              style={{
                fontSize: isMobile ? 28 : 42,
                fontWeight: 300,
                letterSpacing: "-0.02em",
                marginBottom: 16,
              }}
            >
              {t.coreTitle1}{" "}
              <span style={{ fontWeight: 600 }}>{t.coreTitle2}</span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.5)",
                maxWidth: 560,
                lineHeight: 1.8,
              }}
            >
              {t.coreSub}
            </p>
          </div>
        </FadeIn>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, 1fr)"
              : "repeat(4, minmax(0, 1fr))",
            gap: 14,
            alignItems: "stretch",
          }}
        >
          {coreItems[lang].map((item, i) => (
            <CoreCard key={item.title} item={item} index={i} t={t} />
          ))}
        </div>
      </section>

      {/* SOLUTIONS */}
      <section
        id="solutions"
        style={{
          background: "#152030",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: isMobile ? "60px 20px" : "100px 40px",
          }}
        >
          <FadeIn>
            <div style={{ marginBottom: 60 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#B2CD38",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginBottom: 16,
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                {t.solutionsLabel}
              </div>
              <h2
                style={{
                  fontSize: isMobile ? 28 : 42,
                  fontWeight: 300,
                  letterSpacing: "-0.02em",
                  marginBottom: 16,
                }}
              >
                {t.solutionsTitle1}{" "}
                <span style={{ fontWeight: 600 }}>{t.solutionsTitle2}</span>
              </h2>
              <p
                style={{
                  fontSize: 16,
                  color: "rgba(255,255,255,0.5)",
                  maxWidth: 520,
                  lineHeight: 1.8,
                }}
              >
                {t.solutionsSub}
              </p>
            </div>
          </FadeIn>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: 14,
            }}
          >
            {solutions[lang].map((item, i) => (
              <SolutionCard key={item.title} item={item} index={i} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section
        id="products"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: isMobile ? "60px 20px" : "100px 40px",
        }}
      >
        <FadeIn>
          <div style={{ marginBottom: 60 }}>
            <div
              style={{
                fontSize: 11,
                color: "#B2CD38",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                marginBottom: 16,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {t.productsLabel}
            </div>
            <h2
              style={{
                fontSize: isMobile ? 28 : 42,
                fontWeight: 300,
                letterSpacing: "-0.02em",
                marginBottom: 16,
              }}
            >
              {t.productsTitle1}{" "}
              <span style={{ fontWeight: 600 }}>{t.productsTitle2}</span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.5)",
                maxWidth: 520,
                lineHeight: 1.8,
              }}
            >
              {t.productsSub}
            </p>
          </div>
        </FadeIn>
        {/* Row 1 — Ready */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, 1fr)"
              : "repeat(4, minmax(0, 1fr))",
            gap: 14,
            alignItems: "stretch",
            marginBottom: 14,
          }}
        >
          {products[lang]
            .filter((p) => p.ready)
            .map((item, i) => (
              <ProductCard key={item.title} item={item} index={i} t={t} />
            ))}
        </div>
        {/* Row 2 — Coming Soon */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, 1fr)"
              : "repeat(4, minmax(0, 1fr))",
            gap: 14,
            alignItems: "stretch",
          }}
        >
          {products[lang]
            .filter((p) => !p.ready)
            .map((item, i) => (
              <ProductCard key={item.title} item={item} index={i + 4} t={t} />
            ))}
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        style={{
          background: "#152030",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: isMobile ? "60px 20px" : "100px 40px",
          }}
        >
          <FadeIn>
            <div
              style={{
                fontSize: 11,
                color: "#B2CD38",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                marginBottom: 16,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {t.contactLabel}
            </div>
            <h2
              style={{
                fontSize: isMobile ? 28 : 42,
                fontWeight: 300,
                letterSpacing: "-0.02em",
                marginBottom: 16,
                lineHeight: 1.2,
              }}
            >
              {t.contactTitle}
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.8,
                marginBottom: 48,
                maxWidth: 520,
              }}
            >
              {t.contactSub}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 16 : 40,
                marginBottom: 48,
              }}
            >
              {[
                {
                  label: "Email",
                  value: "hello@kibrit.tech",
                  href: "mailto:hello@kibrit.tech",
                },
                {
                  label: "Web",
                  value: "kibrit.tech",
                  href: "https://kibrit.tech",
                },
                {
                  label: t.contactLocation,
                  value: "Baku, Azerbaijan",
                  href: null,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{ display: "flex", alignItems: "baseline", gap: 14 }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#B2CD38",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      minWidth: 70,
                      fontFamily: "'DM Mono', monospace",
                    }}
                  >
                    {item.label}
                  </div>
                  {item.href ? (
                    <a
                      href={item.href}
                      style={{
                        fontSize: 15,
                        color: "rgba(255,255,255,0.7)",
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#fff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "rgba(255,255,255,0.7)")
                      }
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div
                      style={{ fontSize: 15, color: "rgba(255,255,255,0.7)" }}
                    >
                      {item.value}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Social links */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {[
                {
                  label: "WhatsApp",
                  href: "https://wa.me/994553550434",
                  color: "#25D366",
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#25D366"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  ),
                },
                {
                  label: "Telegram",
                  href: "https://t.me/+994553550434",
                  color: "#29B6F6",
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#29B6F6"
                    >
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                  ),
                },
                {
                  label: "LinkedIn",
                  href: "https://www.linkedin.com/company/kibrit",
                  color: "#0A66C2",
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#0A66C2"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  ),
                },
                {
                  label: "Instagram",
                  href: "https://instagram.com/kibrit.tech",
                  color: "#E1306C",
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#E1306C"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                    </svg>
                  ),
                },
                {
                  label: "Facebook",
                  href: "https://www.facebook.com/kibrittech",
                  color: "#1877F2",
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#1877F2"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.label}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "#1a2738",
                    border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    transition: "all 0.2s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = s.color + "60";
                    e.currentTarget.style.background = s.color + "15";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.08)";
                    e.currentTarget.style.background = "#1a2738";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          padding: isMobile ? "20px 20px" : "28px 40px",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Logo height={18} />
            </button>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>
              © 2026 Kibrit
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              {
                href: "https://wa.me/994553550434",
                color: "#25D366",
                icon: (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="#25D366"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                ),
              },
              {
                href: "https://t.me/+994553550434",
                color: "#29B6F6",
                icon: (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="#29B6F6"
                  >
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                ),
              },
              {
                href: "https://www.linkedin.com/company/kibrit",
                color: "#0A66C2",
                icon: (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="#0A66C2"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                ),
              },
              {
                href: "https://instagram.com/kibrit.tech",
                color: "#E1306C",
                icon: (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="#E1306C"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                  </svg>
                ),
              },
              {
                href: "https://www.facebook.com/kibrittech",
                color: "#1877F2",
                icon: (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="#1877F2"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                ),
              },
            ].map((s, i) => (
              <a
                key={i}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = s.color + "18";
                  e.currentTarget.style.borderColor = s.color + "50";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {s.icon}
              </a>
            ))}
          </div>
          <span
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.2)",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            {t.footerTagline}
          </span>
        </div>
      </footer>

      <ChatWidget lang={lang} />
    </div>
  );
}
